import React, { useEffect, useMemo, useState } from 'react';
import { FooterTypes } from 'components/footer/footer.types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section } from 'components/navbar/navbar.interfaces';
import {
  User_Notification_Status_Enum,
  useUpdateInAppNotificationMutation,
} from 'graphql/generated/hasura';

import { mapNotification } from 'utilities/functions';
import { Notification } from 'app/dashboard/interfaces/notifications.interfaces';
import { useAuth } from 'auth/context/AuthContext';
import { NOTIFICATIONS } from 'utilities/routes';
import { messageTemplatesIds } from 'utilities/constants';
import { useGetMessageTemplates } from 'hooks/useGetMessageTemplates';
import { useModalParams } from 'components/modal/useModalManager';
import Navbar from 'components/navbar/navbarComponent';
import Footer from 'components/footer/footerComponent';
import NavbarMobile from 'components/navbar/navbarMobileComponent';
import DownloadReport from 'app/my-skin/components/downloadReport';
import { ModalButtonProps } from 'components/modal/modal.interfaces';
import {
  IdleTimerContext,
  useIdleTimerModalFunctionality,
} from 'hooks/useIdleTimerFunctionality';
import InactivityModal from 'components/InactivityModal';

import { Roles } from '../firebase/interfaces';
import ProviderNavbarMobile from 'components/navbar/ProviderNavbarMobileComponent';
import {
  CustomNotificationActions,
  useHandleExtraActions,
} from 'hooks/useHandleExtraActions';
import ProviderNavbarNotApprovedMobile from 'components/navbar/ProviderNavbarNotApprovedMobileComponent';
import { NotificationsProvider } from 'notifications/contexts/NotificationsProvider';
import { ModalProvider } from 'layout/ModalProvider';
import SuperAdminNavbarMobile from 'components/navbar/SuperAdminNavbarMobile';
import { Toaster } from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';
import { useNotifications } from '../hooks/pageLayout/useNotifications';
import { NotificationsModal } from './NotificationsModal';
import { ToastNotifications } from './ToastNotifications';
import { useCustomToast } from './useToast';
import Alert from '../components/alertComponent';
import { useVerifyChild } from '../app/my-account/pages/child/useVerifyChild';
import { UnverifiedChildMobileNavbar } from '../components/navbar/UnverifiedChildNavbar';
import ChildQuestionnaireBanner from '../app/my-account/pages/child/ChildQuestionnaireBanner';

interface PageLayoutProps {
  children: React.ReactNode;
  footerType?: FooterTypes;
  className?: string;
}

const notificationVariables = {
  isToast: true,
  includeStatuses: [
    User_Notification_Status_Enum.Delivered,
    User_Notification_Status_Enum.Seen,
  ],
};

const modalNotificationVariables = {
  includeStatuses: [
    User_Notification_Status_Enum.Delivered,
    User_Notification_Status_Enum.Seen,
  ],
  isModal: true,
  limit: 1,
  offset: 0,
};

const PageLayoutInternal: React.FC<PageLayoutProps> = ({
  children,
  footerType,
  className,
}) => {
  const navigate = useNavigate();
  const [isNavbarMobileVisible, setNavbarMobileVisible] =
    useState<boolean>(false);
  const [selectedSection, setSelectedSection] = useState<Section>();
  const { toasts, handleAction: handleCustomToastsAction } = useCustomToast();
  const location = useLocation();
  const { isOpen: isDownloadReportWarningOpen } =
    useModalParams('download-warning');

  const toggleNavMobile = () => {
    setNavbarMobileVisible(!isNavbarMobileVisible);
  };
  const { user, selectedAccount, stripeSubscription } = useAuth();
  const { isVerify } = useVerifyChild();

  const userRole = user?.role;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [modalNotification, setModalNotification] =
    useState<Notification | null>(null);

  const { handler: customActionsHandler } = useHandleExtraActions();

  const { notifications: notificationsData } = useNotifications(
    notificationVariables,
  );

  const { notifications: modalNotificationsData } = useNotifications(
    modalNotificationVariables,
  );

  useEffect(() => {
    if (notificationsData) {
      const populateNotifications: Notification[] = notificationsData
        .filter((notification) => notification.is_toast)
        .map(mapNotification);
      setNotifications(populateNotifications);
    }
  }, [notificationsData]);

  useEffect(() => {
    if (modalNotificationsData) {
      const notification = modalNotificationsData[0];
      if (!notification) {
        setModalNotification(null);
        return;
      }

      setModalNotification(mapNotification(notification));
    }
  }, [modalNotificationsData, setModalNotification]);

  const [updateInAppNotificationMutation] = useUpdateInAppNotificationMutation(
    {},
  );

  const { data: locale, loading } = useGetMessageTemplates({
    locale: 'en',
    templateId: [
      messageTemplatesIds.MY_PROFILE,
      messageTemplatesIds.MY_PERSONAL_INFO,
      messageTemplatesIds.MY_CONTACT_INFO,
      messageTemplatesIds.HEALTH_QUESTIONNAIRE,
    ],
  });

  const {
    inactivityModalOpen,
    handleStillHere,
    handleLogOut,
    componentLoading,
    componentLocale,
    remainingTime,
    pause,
    resume,
  } = useIdleTimerModalFunctionality();

  const timerContextValue = useMemo(
    () => ({
      pauseTimer: pause,
      resumeTimer: resume,
    }),
    [pause, resume],
  );

  const handleAction = async (
    notificationId: string,
    route: string,
    customAction?: CustomNotificationActions,
    customActionParams?: Record<string, unknown>,
    isCloseButton = false,
  ) => {
    const updateNotificationVariable = {
      id: String(notificationId),
      status: User_Notification_Status_Enum.Read,
    };

    try {
      if (isCloseButton) {
        updateInAppNotificationMutation({
          variables: updateNotificationVariable,
        });
        return;
      }
      if (customAction && customActionsHandler[customAction]) {
        const [response] = await Promise.all([
          updateInAppNotificationMutation({
            variables: updateNotificationVariable,
          }),
          customActionsHandler[customAction](customActionParams ?? {}, false),
        ]);
        if (!response.data) {
          throw new Error('Failed to update notification');
        }
      } else {
        navigate((route || '') ?? -1);

        const response = await updateInAppNotificationMutation({
          variables: updateNotificationVariable,
        });
        if (!response.data) {
          throw new Error('Failed to update notification');
        }
      }
    } catch (error: unknown) {
      throw new Error('Failed to update notification');
    }
    return;
  };

  useEffect(() => {
    const route = location.pathname.split('/')[1];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location, selectedSection]);

  const getAuthenticatedNavbarBasedOnUserRole = () => {
    switch (userRole) {
      case Roles.PATIENT:
        if (!isVerify && selectedAccount) {
          return (
            <UnverifiedChildMobileNavbar closeNavbarMobile={toggleNavMobile} />
          );
        }
        return <NavbarMobile closeNavbarMobile={toggleNavMobile} />;
      case Roles.PROVIDER:
        if (user?.providerEnabled && stripeSubscription?.length) {
          return <ProviderNavbarMobile closeNavbarMobile={toggleNavMobile} />;
        }
        return (
          <ProviderNavbarNotApprovedMobile
            closeNavbarMobile={toggleNavMobile}
          />
        );
      case Roles.SUPERADMIN:
      case Roles.DELEGATED_ADMIN:
        return <SuperAdminNavbarMobile closeNavbarMobile={toggleNavMobile} />;
      default:
        return <NavbarMobile closeNavbarMobile={toggleNavMobile} />;
    }
  };

  const InactivityModalButtons: ModalButtonProps[] = [
    {
      label: componentLocale?.CTA2Label,
      onClick: handleStillHere,
      testID: 'still-here',
    },
    {
      label: componentLocale?.CTA1Label,
      onClick: handleLogOut,
      className: 'text-clc-blue bg-white',
      testID: 'log-out',
    },
  ];

  if ((loading && !locale) || (componentLoading && !componentLocale))
    return null;

  return (
    <>
      {isDownloadReportWarningOpen && <DownloadReport />}

      {inactivityModalOpen && (
        <InactivityModal
          inactivityModalOpen={inactivityModalOpen}
          title={componentLocale?.title}
          message={componentLocale?.message}
          timerMessage={componentLocale?.timerMessage}
          remainingTime={remainingTime}
          continueMessage={componentLocale?.continueMessage}
          buttons={InactivityModalButtons}
          onClose={() => handleStillHere()}
        />
      )}

      {modalNotification && (
        <NotificationsModal
          notification={modalNotification}
          handleClose={() => {
            handleAction(
              modalNotification?.id,
              modalNotification.action?.route ?? NOTIFICATIONS,
              modalNotification.action?.customAction,
              modalNotification.action?.customActionParams,
              true,
            )
              .then(() => setModalNotification(null))
              .catch((e) => console.error(e));
          }}
        />
      )}

      {isNavbarMobileVisible ? (
        getAuthenticatedNavbarBasedOnUserRole()
      ) : (
        <div className="relative">
          <div className="z-[1050] sticky top-0">
            <Navbar openNavbarMobile={toggleNavMobile} />
          </div>
          <div
            className={twMerge(
              'flex relative flex-col justify-between bg-base-content min-h-content-page-mobile desktop:min-h-content-page-web',
              className,
            )}
          >
            <div className="flex desktop:gap-30 gap-5 flex-col grow h-full pb-10 px-4 desktop:py-[60px] 2xl:px-[108px]">
              <div className="relative">
                <div
                  data-testid="layout-api-toasts"
                  className={twMerge(
                    'absolute flex flex-col gap-2.5 desktop:-top-10 w-full',
                    [...notifications, ...toasts].length && 'z-[1000]',
                  )}
                >
                  <ToastNotifications
                    toastNotifications={notifications}
                    handleAction={handleAction}
                  />
                  <div
                    data-testid="layout-custom-toasts"
                    className="flex flex-col gap-2.5 w-full"
                  >
                    {toasts.map((toast) => {
                      return (
                        <div key={toast.id}>
                          <Alert
                            type={toast.type}
                            text={toast.message}
                            hideCloseButton={!toast.showCloseButton}
                            isNotification={toast.showCloseButton}
                            onClose={() =>
                              handleCustomToastsAction(
                                toast.id,
                                toast.action?.route,
                                toast.action?.customAction,
                                toast.action?.customActionParams,
                                true,
                              )
                            }
                            actionLabel={toast.action?.label}
                            onActionClick={() =>
                              handleCustomToastsAction(
                                toast.id,
                                toast.action?.route,
                                toast.action?.customAction,
                                toast.action?.customActionParams,
                                false,
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {selectedAccount && <ChildQuestionnaireBanner />}

              <IdleTimerContext.Provider value={timerContextValue}>
                {children}
              </IdleTimerContext.Provider>
            </div>
            <Footer type={footerType} />
          </div>
        </div>
      )}
    </>
  );
};

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  footerType,
  className,
}) => {
  return (
    <>
      <ModalProvider>
        <NotificationsProvider>
          <PageLayoutInternal footerType={footerType} className={className}>
            {children}
          </PageLayoutInternal>
        </NotificationsProvider>
      </ModalProvider>
      <Toaster position="top-center" reverse-order={false} />
    </>
  );
};

export default PageLayout;
